/**
 * Chummys box — <chummys-box>
 *
 * Two small jobs: write --box-p (0 as the section enters at the bottom of the
 * viewport, 1 as it leaves at the top) for the ingredient drift and the glow,
 * and tilt the box a few degrees towards the pointer on hover so it reads as
 * a thing rather than a picture. `is-in` fires the reveals once.
 */
(() => {
  if (customElements.get('chummys-box')) return;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  class ChummysBox extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.chummys-box__frame');
      this.img = this.querySelector('.chummys-box__img');
      this.onScroll = this.onScroll.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onLeave = this.onLeave.bind(this);

      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { this.classList.add('is-in'); this.io.disconnect(); }
      }, { threshold: 0.18 });
      this.io.observe(this);

      if (!mqReduced.matches) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
        if (this.frame && mqHover.matches) {
          this.frame.addEventListener('pointermove', this.onMove);
          this.frame.addEventListener('pointerleave', this.onLeave);
        }
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.frame) { this.frame.removeEventListener('pointermove', this.onMove); this.frame.removeEventListener('pointerleave', this.onLeave); }
      if (this.io) this.io.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        const r = this.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const p = clamp((vh - r.top) / (vh + r.height), 0, 1);
        this.style.setProperty('--box-p', p.toFixed(4));
      });
    }

    /* the tilt: at most 5 degrees, about the box's centre */
    onMove(e) {
      const r = this.frame.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      this.frame.classList.remove('is-resting');
      this.img.style.setProperty('--tilt-y', (x * 10).toFixed(2) + 'deg');
      this.img.style.setProperty('--tilt-x', (-y * 8).toFixed(2) + 'deg');
    }

    onLeave() {
      this.frame.classList.add('is-resting');
      this.img.style.setProperty('--tilt-x', '0deg');
      this.img.style.setProperty('--tilt-y', '0deg');
    }
  }

  customElements.define('chummys-box', ChummysBox);
})();
